import React from "react";
import { StyleSheet, View, ScrollView, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";

export interface IAiToolbarProps {
  visible?: boolean;
  onEnhanceText?: () => void;
  onSpellCheck?: () => void;
  onAutoComplete?: () => void;
  onTranslate?: () => void;
  onBuildMemoryNarrative?: () => void;
  onAskAi?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * AiToolbar
 *
 * Floating toolbar providing AI-assisted narrative enhancement, grammar & spelling,
 * smart auto-completion, multi-language translation, and memory Q&A.
 */
export const AiToolbar: React.FC<IAiToolbarProps> = ({
  visible = true,
  onEnhanceText,
  onSpellCheck,
  onAutoComplete,
  onTranslate,
  onBuildMemoryNarrative,
  onAskAi,
  isStandalonePreview = false,
  style,
}) => {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const isDark = cardBg === "#131520";

  if (!visible) return null;

  const handleMouseDown = (e: any) => {
    if (Platform.OS === "web") {
      e.preventDefault();
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: cardBg,
          borderColor,
          boxShadow: isDark
            ? "0 4px 16px rgba(0,0,0,0.5)"
            : "0 4px 16px rgba(0,0,0,0.1)",
        } as any,
        isStandalonePreview && styles.standaloneContainer,
        style,
      ]}
      onMouseDown={handleMouseDown}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <ThemedText style={styles.sectionLabel}>AI Assistant:</ThemedText>

        {/* AI Polish / Enhance */}
        <ThemedToolTip tooltip="Refine Tone & Enhance Memory Description">
          <ThemedButton
            title="Enhance"
            variant="ghost"
            icon={<Ionicons name="sparkles-outline" size={15} color={tintColor} />}
            onPress={onEnhanceText}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Spell & Grammar */}
        <ThemedToolTip tooltip="Check Spelling & Grammar">
          <ThemedButton
            title="Spell Check"
            variant="ghost"
            icon={<Ionicons name="checkmark-done-outline" size={15} color={tintColor} />}
            onPress={onSpellCheck}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Auto-Complete */}
        <ThemedToolTip tooltip="Suggest Auto-Completion">
          <ThemedButton
            title="Auto-Complete"
            variant="ghost"
            icon={<Ionicons name="flash-outline" size={15} color={tintColor} />}
            onPress={onAutoComplete}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Translation */}
        <ThemedToolTip tooltip="Translate Memory into Other Languages">
          <ThemedButton
            title="Translate"
            variant="ghost"
            icon={<Ionicons name="language-outline" size={15} color={tintColor} />}
            onPress={onTranslate}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Build Narrative from Media & Contacts */}
        <ThemedToolTip tooltip="Synthesize Memory Story from Photos & People">
          <ThemedButton
            title="Build Story"
            variant="ghost"
            icon={<Ionicons name="bulb-outline" size={15} color={tintColor} />}
            onPress={onBuildMemoryNarrative}
            style={styles.actionBtn}
          />
        </ThemedToolTip>

        {/* Ask AI */}
        <ThemedToolTip tooltip="Ask AI Questions About this Memory">
          <ThemedButton
            title="Ask AI"
            variant="ghost"
            icon={<Ionicons name="chatbubble-ellipses-outline" size={15} color={tintColor} />}
            onPress={onAskAi}
            style={styles.actionBtn}
          />
        </ThemedToolTip>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 38,
  },
  standaloneContainer: {
    position: "relative",
    top: 0,
    left: 0,
  },
  scrollContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.6,
    marginRight: 4,
  },
  actionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 28,
  },
});
